import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import { useSerpCount } from '@/hooks/useSourcePosts';
import { KLUCZ_MOJEGO_WYSZUKIWANIA, saveAlerts, saveMyFilters, touchViewed, type MojeWyszukiwanie } from '@/hooks/useMojeWyszukiwanie';
import type { SavedSearchCadence } from '@/types/savedSearch';
import { wyczyszczoneFiltry } from '@/lib/serpUrl';
import {
  akcjaFlagi,
  godzinOd,
  kluczDymka,
  kluczZbioru,
  opisZbioru,
  maCene,
  pokazacDymek,
  pytacOMaile,
  roznicaZbiorow,
  stanFlagi,
  zbiorOdObejrzenia,
  type StanFlagi,
} from '@/lib/mojeFiltry';

/**
 * MOJE FILTRY — FLAGA I „ZAPISZ” NA STRONIE WYNIKÓW (build plan C1, plan E1.3, §1.2–1.9, §1.71, §7).
 * Reguły stanu są w `src/lib/mojeFiltry.ts`; tu jest pamięć (co było edycją, animacja zapisu,
 * jednorazowy dymek), zapis przez `save_my_filters` i komunikaty. Komunikat z „Cofnij” ma TYLKO
 * zapis (pierwszy też — jego „Cofnij” wraca do niezapisanego wypełnienia, migracja
 * 20261016001251). Włączenie i wyłączenie flagi oraz odrzucenie zmian są ciche (§1.74, Maciek
 * 16.10): to odwracalne dotknięcie, drugie dotknięcie flagi je cofa.
 *
 * PIERWSZY zapis nie ma komunikatu: zamiast niego otwiera się jednorazowe pytanie o e-maile (C3,
 * plan E1.5, §1.25) — jego własna linijka „Moje filtry są gotowe” potwierdza zapis, a każdy komunikat
 * na ekranie zamyka się przed nim. Kiedy pytać: `pytacOMaile` w `src/lib/mojeFiltry.ts`.
 *
 * Tylko najemca (`moje.isTenant`): gość, właściciel i agent nie widzą flagi (`widoczna: false`).
 */

/** Ekran po „Cofnij” zapisu różni się od zbioru z powodu zmiany człowieka — „Zapisz” wraca. */
const ZAWSZE_EDYTOWANE = '\u0000edytowane';
/** Jednorazowy dymek pod flagą (§1.8) — wygoda jednej przeglądarki, nie stan konta. */
export const KLUCZ_DYMKA = 'flh-moje-filtry-dymek';
/** Ptaszek na fladze przed zapaleniem (makieta: 700 ms), przekreślenie „Zapisz” (450 ms). */
export const CZAS_PTASZKA_MS = 700;
export const CZAS_PRZEKRESLENIA_MS = 450;
/** Pytanie o e-maile po pierwszym zapisie czeka 900 ms (makieta), żeby widać było, jak flaga się zapala, a „Zapisz” chowa. */
export const CZAS_DO_PYTANIA_MS = 900;
/** Jednorazowy dymek wychodzi 300 ms po wejściu (makieta `maybeHint`), gdy układ już stoi. */
export const CZAS_DO_DYMKA_MS = 300;
/** „Zapisano…” z „Cofnij” wisi 5 s (§1.74, Maciek 16.10); odpowiedź na pytanie o e-maile 4,5 s. */
export const CZAS_KOMUNIKATU_ZAPISU_MS = 5000;
export const CZAS_KOMUNIKATU_MS = 4500;

const czekaj = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

function dymekWidziany(): boolean {
  try { return window.localStorage.getItem(KLUCZ_DYMKA) === '1'; } catch { return false; }
}

export interface MojeFiltryStrony extends StanFlagi {
  /** Najemca (ma wiersz zbioru, zapisany albo tylko wypełniony) — tylko wtedy jest flaga. */
  widoczna: boolean;
  /** Ptaszek na fladze w trakcie zapisu. */
  sprawdza: boolean;
  /** „Zapisz” przekreślone przed schowaniem (odrzucenie zmian). */
  przekreslone: boolean;
  /** Ostatni zapis odrzucony przez bazę (§1.3 i inne) — „Zapisz” mówi o błędzie, dopóki ktoś nie spróbuje znowu. */
  blad: boolean;
  /** Nowe oferty w zbiorze od ostatniego obejrzenia (znaczek „+N” na zgaszonej fladze). */
  nowe: number;
  /** Opis zbioru do podpowiedzi („Włącz moje filtry: …”). */
  opis: string;
  /** Klucz tekstu dymka — `null`, gdy dymka nie ma. */
  dymek: string | null;
  zamknijDymek: () => void;
  naFlage: () => void;
  /** `false`, gdy zapis nie ruszył (bez ceny — komponent pokazuje „Wybierz cenę, żeby zapisać”). */
  naZapisz: () => boolean;
  /** Zapis podanego stanu (szkic panelu z zaznaczonym „Zapisz…”, C2); `false`, gdy bez ceny. */
  zapiszZbior: (f: SourcePostFilters) => boolean;
  /** Jednorazowe pytanie o e-maile otwarte (C3). */
  pytanie: boolean;
  /** Odpowiedź: kadencja = „Tak, włącz alerty”, `null` = „Nie, dziękuję”. `false`, gdy zapis się nie udał (pytanie zostaje). */
  odpowiedz: (kadencja: SavedSearchCadence | null) => Promise<boolean>;
  /** Zamknięcie bez odpowiedzi (Esc, tło): nic nie idzie do bazy, powiadomienia zostają wyłączone. */
  zamknijPytanie: () => void;
  /** Strona wołała × albo „Wyczyść”: to nie edycja, więc „Zapisz” się nie wysuwa (§1.3). */
  oznaczBezEdycji: (f: SourcePostFilters) => () => void;
}

export function useMojeFiltry({
  filters,
  moje,
  ustawFiltry,
  toasterId,
  zablokowany = false,
  poWylaczeniu,
}: {
  filters: SourcePostFilters;
  moje: MojeWyszukiwanie | undefined;
  /** Zmiana filtrów, która NIE jest edycją człowieka (wczytanie zbioru, wyłączenie, „Cofnij”). */
  ustawFiltry: (f: SourcePostFilters) => void;
  toasterId?: string;
  /** Panel albo arkusz otwarty: dymek czeka (toast i dymek nigdy nie leżą na panelu, §1.25). */
  zablokowany?: boolean;
  /** Po wyłączeniu flagi (strona odrzuca wtedy start z profilu na tę sesję). */
  poWylaczeniu?: () => void;
}): MojeFiltryStrony {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const widoczna = !!moje?.isTenant;
  const zapisane = moje && moje.savedAt !== null ? moje.filters : null;

  // „Baza” = klucz stanu, który NIE był edycją (wejście, wczytanie zbioru, wyłączenie, ×).
  // Ekran różny od bazy = człowiek coś zmienił. `null` do chwili, gdy przyjdzie zbiór.
  const [baza, setBaza] = useState<string | null>(null);
  const klucz = kluczZbioru(filters);
  const edytowano = baza !== null && klucz !== baza;
  const stan = stanFlagi({ zapisane, ekran: filters, edytowano });

  useEffect(() => {
    if (!moje) return;
    // Pierwszy stan po wczytaniu zbioru i każdy stan „ekran = zbiór” to nowa baza (makieta: lit → edited = false).
    if (baza === null || (stan.zapalona && baza !== klucz)) setBaza(klucz);
  }, [moje, baza, stan.zapalona, klucz]);

  const [sprawdza, setSprawdza] = useState(false);
  const [przekreslone, setPrzekreslone] = useState(false);
  const [blad, setBlad] = useState(false);
  useEffect(() => { setBlad(false); }, [klucz]);

  /* ---------- +N: nowe oferty od ostatniego obejrzenia (§1.6) ---------- */
  const godzin = godzinOd(moje?.lastViewedAt);
  const { data: noweZAPI } = useSerpCount(zapisane && godzin !== null ? zbiorOdObejrzenia(zapisane, godzin) : {}, {
    enabled: widoczna && !!zapisane && !stan.zapalona && godzin !== null,
  });
  const nowe = stan.zapalona || godzin === null ? 0 : noweZAPI ?? 0;
  // Zbiór na ekranie = obejrzany: znaczek liczy od teraz. Raz na zapalenie.
  const obejrzane = useRef(false);
  useEffect(() => {
    if (!stan.zapalona) { obejrzane.current = false; return; }
    if (obejrzane.current || !widoczna) return;
    obejrzane.current = true;
    touchViewed().then((m) => qc.setQueryData(KLUCZ_MOJEGO_WYSZUKIWANIA, m)).catch(() => { /* znaczek policzy się od poprzedniego razu */ });
  }, [stan.zapalona, widoczna, qc]);

  /* ---------- jednorazowy dymek (§1.8; powitalny przy niezapisanym zbiorze, §1.71) ---------- */
  const [widziany, setWidziany] = useState(dymekWidziany);
  const zamknijDymek = useCallback(() => {
    setWidziany(true);
    try { window.localStorage.setItem(KLUCZ_DYMKA, '1'); } catch { /* tryb prywatny: wróci w następnej sesji */ }
  }, []);
  const [pytanie, setPytanie] = useState(false);
  const [poWejsciu, setPoWejsciu] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setPoWejsciu(true), CZAS_DO_DYMKA_MS);
    return () => window.clearTimeout(id);
  }, []);
  const dymek = widoczna && poWejsciu && !zablokowany && !pytanie && pokazacDymek(stan, widziany) ? kluczDymka(stan) : null;

  const lang = i18n.language;
  const opis = zapisane ? opisZbioru(zapisane, t, lang) : '';

  /* ---------- akcje ---------- */
  const komunikat = useCallback(
    (tekst: string, cofnij: () => void, czas = CZAS_KOMUNIKATU_MS) =>
      toast(tekst, { toasterId, duration: czas, action: { label: t('search.area.undo'), onClick: cofnij } }),
    [t, toasterId],
  );

  /** Ekran → `f` bez edycji; zwraca „Cofnij”, które przywraca poprzedni ekran i jego bazę. */
  const przestaw = useCallback(
    (f: SourcePostFilters) => {
      const przed = filters;
      const bazaPrzed = baza;
      ustawFiltry(f);
      setBaza(kluczZbioru(f));
      return () => { ustawFiltry(przed); setBaza(bazaPrzed); };
    },
    [filters, baza, ustawFiltry],
  );

  // Ciche (§1.74): odwracalne drugim dotknięciem flagi, więc bez komunikatu i bez „Cofnij”.
  const wlacz = useCallback(() => {
    if (!zapisane) return;
    przestaw({ ...wyczyszczoneFiltry(filters), ...zapisane });
  }, [zapisane, filters, przestaw]);

  const zapiszZbior = useCallback((f: SourcePostFilters): boolean => {
    if (!widoczna || !moje || sprawdza) return true;
    if (!maCene(f)) return false;
    zamknijDymek();
    const poprzednie = moje;
    setSprawdza(true);
    setBlad(false);
    void (async () => {
      try {
        const [nowe] = await Promise.all([saveMyFilters(f, { saved: true }), czekaj(CZAS_PTASZKA_MS)]);
        qc.setQueryData(KLUCZ_MOJEGO_WYSZUKIWANIA, nowe);
        setBaza(kluczZbioru(f));
        const pierwszy = poprzednie.savedAt === null;
        if (pytacOMaile(poprzednie, nowe)) {
          // Pytanie zastępuje „Zapisano” i nic nie leży na nim (§1.25). Najpierw ruch zapisu (makieta: 900 ms).
          toast.dismiss();
          window.setTimeout(() => { toast.dismiss(); setPytanie(true); }, CZAS_DO_PYTANIA_MS);
          return;
        }
        const roznica = pierwszy ? '' : roznicaZbiorow(poprzednie.filters, nowe.filters, t, lang);
        const tekst = pierwszy
          ? t('search.my_filters.saved_first', { set: opisZbioru(nowe.filters, t, lang) }) + (nowe.alerts.enabled ? t('search.my_filters.alerts_on') : '')
          : (roznica ? t('search.my_filters.saved_diff', { diff: roznica }) : t('search.my_filters.saved')) + (nowe.alerts.enabled ? t('search.my_filters.alerts_updated') : '');
        komunikat(tekst, () => {
          // „Cofnij” zapisu: zbiór wraca do poprzedniego (pierwszy zapis — do niezapisanego
          // wypełnienia), ekran zostaje, więc „Zapisz” znów jest wysunięte.
          saveMyFilters(poprzednie.filters, { saved: !pierwszy })
            .then((m) => { qc.setQueryData(KLUCZ_MOJEGO_WYSZUKIWANIA, m); setBaza(ZAWSZE_EDYTOWANE); })
            .catch(() => toast.error(t('search.my_filters.undo_error'), { toasterId }));
        }, CZAS_KOMUNIKATU_ZAPISU_MS);
      } catch {
        setBlad(true);
        toast.error(t('search.my_filters.error'), { toasterId });
      } finally {
        setSprawdza(false);
      }
    })();
    return true;
  }, [widoczna, moje, sprawdza, zamknijDymek, qc, t, lang, komunikat, toasterId]);

  const zapisz = useCallback((): boolean => zapiszZbior(filters), [zapiszZbior, filters]);

  const zamknijPytanie = useCallback(() => setPytanie(false), []);
  const odpowiedz = useCallback(
    async (kadencja: SavedSearchCadence | null): Promise<boolean> => {
      // „Nie, dziękuję”: powiadomienia są już wyłączone (pierwszy zapis ich nie włącza) — nic do zapisania.
      if (kadencja === null) { setPytanie(false); return true; }
      try {
        const nowe = await saveAlerts({ enabled: true, cadence: kadencja });
        qc.setQueryData(KLUCZ_MOJEGO_WYSZUKIWANIA, nowe);
        setPytanie(false);
        toast(t(`search.my_filters.mail.done_${kadencja}`), { toasterId, duration: CZAS_KOMUNIKATU_MS });
        return true;
      } catch {
        return false;
      }
    },
    [qc, t, toasterId],
  );

  const naFlage = useCallback(() => {
    if (!widoczna || sprawdza || przekreslone) return;
    zamknijDymek();
    const akcja = akcjaFlagi(stan);
    if (akcja === 'zapisz') { zapisz(); return; }
    if (akcja === 'wylacz') {
      // Najpierw strona (odrzucenie startu z profilu ustawia domyślne filtry), potem czysty ekran z zachowanym widokiem.
      poWylaczeniu?.();
      przestaw(wyczyszczoneFiltry(filters));
      return;
    }
    if (akcja === 'odrzuc') {
      // „Nie zapisuj, wróć do Moich filtrów”: „Zapisz” przekreśla się, potem chowa (Maciek 15.10).
      setPrzekreslone(true);
      window.setTimeout(() => { setPrzekreslone(false); wlacz(); }, CZAS_PRZEKRESLENIA_MS);
      return;
    }
    wlacz();
  }, [widoczna, sprawdza, przekreslone, zamknijDymek, stan, zapisz, przestaw, filters, poWylaczeniu, wlacz]);

  const oznaczBezEdycji = useCallback(
    (f: SourcePostFilters) => {
      const bazaPrzed = baza;
      setBaza(kluczZbioru(f));
      return () => setBaza(bazaPrzed);
    },
    [baza],
  );

  return useMemo(
    () => ({ ...stan, widoczna, sprawdza, przekreslone, blad, nowe, opis, dymek, zamknijDymek, naFlage, naZapisz: zapisz, zapiszZbior, pytanie, odpowiedz, zamknijPytanie, oznaczBezEdycji }),
    [stan, widoczna, sprawdza, przekreslone, blad, nowe, opis, dymek, zamknijDymek, naFlage, zapisz, zapiszZbior, pytanie, odpowiedz, zamknijPytanie, oznaczBezEdycji],
  );
}
